import { HttpTypes } from "@medusajs/types"

// ============ VARIANT OPTIONS KEYMAP ============

export default function getVariantOptionsKeymap(
  variantOptions: HttpTypes.StoreProductVariant["options"]
): Record<string, string> | undefined {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: HttpTypes.StoreProductOptionValue) => {
    acc[varopt.option_id!] = varopt.value
    return acc
  }, {})
}

// Also export as named export for flexibility
export { getVariantOptionsKeymap }

// ============ VARIANT IN STOCK ============

export function isVariantInStock(variant: HttpTypes.StoreProductVariant): boolean {
  return !variant.manage_inventory || variant.allow_backorder || (
    variant.manage_inventory === true &&
    (variant.inventory_quantity || 0) > 0
  )
}

// ============ SORT PRODUCTS TYPE ============

export type ProductSortOptions =
  | "newest"
  | "price_asc"
  | "price_desc"
  | "title_asc"

export function sortToOrderParam(sort: ProductSortOptions): string {
  switch (sort) {
    case "newest":
      return "-created_at"
    case "price_asc":
    case "price_desc":
      return "-created_at"
    case "title_asc":
      return "title"
    default:
      return "-created_at"
  }
}

export function isPriceSort(
  sort: ProductSortOptions
): sort is "price_asc" | "price_desc" {
  return sort === "price_asc" || sort === "price_desc"
}

export type ProductPriceFilter = {
  priceMin: number | null
  priceMax: number | null
}

export function getCheapestProductPriceAmount(
  product: HttpTypes.StoreProduct
): number | null {
  const amounts = (product.variants ?? [])
    .map((variant) => variant.calculated_price?.calculated_amount)
    .filter((amount): amount is number =>
      typeof amount === "number" && Number.isFinite(amount)
    )

  if (amounts.length === 0) {
    return null
  }

  return Math.min(...amounts)
}

export function filterProductsByPrice<T extends HttpTypes.StoreProduct>(
  products: T[],
  { priceMin, priceMax }: ProductPriceFilter
): T[] {
  if (priceMin === null && priceMax === null) {
    return products
  }

  // Prices are in major units (dollars) in Medusa v2, matching the dollar
  // values entered in the price filter — compare directly.
  const minAmount = priceMin
  const maxAmount = priceMax

  return products.filter((product) => {
    const cheapest = getCheapestProductPriceAmount(product)
    if (cheapest === null) return true
    if (minAmount !== null && cheapest < minAmount) return false
    if (maxAmount !== null && cheapest > maxAmount) return false
    return true
  })
}

export function sortProductsByPrice<T extends HttpTypes.StoreProduct>(
  products: T[],
  sort: Extract<ProductSortOptions, "price_asc" | "price_desc">
): T[] {
  return [...products].sort((a, b) => {
    const aPrice = getCheapestProductPriceAmount(a)
    const bPrice = getCheapestProductPriceAmount(b)

    if (aPrice === null && bPrice === null) return 0
    if (aPrice === null) return 1
    if (bPrice === null) return -1

    return sort === "price_asc" ? aPrice - bPrice : bPrice - aPrice
  })
}
